<template>
  <div class="nested-child-wrapper">
    <!-- For each option, check if it has a child_question -->
    <div
      v-for="(optionItem, optIndex) in question.options"
      :key="`${depthKey}-opt-${optIndex}`"
    >
      <div
        v-if="getChildQuestion(optionItem)"
        class="child-question-preview"
        :class="`depth-${depth + 1}`"
      >
        <div class="child-header">
          <div class="child-content">
          <span class="child-trigger">{{ $t(`${i18nPrefix}.label.options`) }} "{{ optionItem.description }}" →</span>
          <span class="child-depth">L{{ depth + 1 }}</span>
          <span class="child-title">{{ getChildQuestion(optionItem).title }}</span>
          <span class="child-type">{{ getTypeLabel(getChildQuestion(optionItem).type) }}</span>
          </div>
          <div v-if="!disabled" class="child-actions">
          <el-button
            v-if="!disabled"
            type="text"
            class="option-action-btn edit-child-btn"
            :aria-label="$t(`${i18nPrefix}.editor.question.title.edit`)"
            @click.stop="$emit('edit-question', getChildQuestion(optionItem))"
          >
            <i class="el-icon-edit-outline"></i>
          </el-button>
          <el-button
            v-if="!disabled"
            type="text"
            class="option-action-btn delete-child-btn"
            :aria-label="$t(`${i18nPrefix}.message.deleteQuestionLabel`)"
            @click.stop="$emit('delete-question', getChildQuestion(optionItem))"
          >
            <i class="iconfont iconDelete"></i>
          </el-button>
          </div>
        </div>
        <!-- 子问题的选项列表（如果有） -->
        <div
          v-if="HAS_OPTIONS_QUESTION_TYPES.includes(getChildQuestion(optionItem).type)"
          class="child-options-container"
        >
          <ul class="option-list child-option-list">
            <li
              v-for="(childOpt, childOptIdx) in getChildQuestion(optionItem).options"
              :key="`${depthKey}-child-${childOptIdx}`"
              :class="{ knockout: isKnockoutOption(childOpt) }"
            >
              <span>{{ childOpt.description }}</span>
              <span
                v-if="getOptionChildLabel(getChildQuestion(optionItem), childOpt)"
                class="next-question-preview"
              >
                → {{ getOptionChildLabel(getChildQuestion(optionItem), childOpt) }}
              </span>
              <el-button
                v-if="!disabled && depth + 1 < maxDepth && !isKnockoutOption(childOpt) && canConfigureChildForParent(getChildQuestion(optionItem))"
                type="text"
                class="option-action-btn"
                @click.stop="$emit('open-dialog', getChildQuestion(optionItem), childOpt, childOptIdx)"
              >
                {{ getOptionChildLabel(getChildQuestion(optionItem), childOpt) ? $t(`${i18nPrefix}.btn.changeChild`) : $t(`${i18nPrefix}.btn.setChild`) }}
              </el-button>
              <el-button
                v-if="!disabled && getOptionChildLabel(getChildQuestion(optionItem), childOpt)"
                type="text"
                class="option-action-btn remove-btn"
                @click.stop="$emit('remove-child', getChildQuestion(optionItem), childOpt, childOptIdx)"
              >
                {{ $t(`${i18nPrefix}.btn.removeChild`) }}
              </el-button>
            </li>
          </ul>
          <!-- RECURSIVE: Render nested child for L2+ levels -->
          <nested-child-question
            v-if="depth < maxDepth - 1"
            :question="getChildQuestion(optionItem)"
            :depth="depth + 1"
            :depth-key="`${depthKey}-nested-${optIndex}`"
            :disabled="disabled"
            :max-depth="maxDepth"
            :has-options-question-types="HAS_OPTIONS_QUESTION_TYPES"
            :child-question-parent-types="childQuestionParentTypes"
            :i18n-prefix="i18nPrefix"
            :questions="questions"
            @open-dialog="(parent, opt, idx) => $emit('open-dialog', parent, opt, idx)"
            @remove-child="(parent, opt, idx) => $emit('remove-child', parent, opt, idx)"
            @edit-question="(q) => $emit('edit-question', q)"
            @delete-question="(q) => $emit('delete-question', q)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { HAS_OPTIONS_QUESTION_TYPES } from '@/constant/openQuestion'

export default {
  name: 'NestedChildQuestion',

  props: {
    question: {
      type: Object,
      required: true
    },
    depth: {
      type: Number,
      default: 0
    },
    depthKey: {
      type: String,
      default: 'root'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    maxDepth: {
      type: Number,
      default: 100
    },
    hasOptionsQuestionTypes: {
      type: Array,
      default: () => HAS_OPTIONS_QUESTION_TYPES
    },
    childQuestionParentTypes: {
      type: Array,
      required: true
    },
    i18nPrefix: {
      type: String,
      default: 'common.openQuestion'
    },
    questions: {
      type: Array,
      default: () => []
    }
  },

  data () {
    return {
      HAS_OPTIONS_QUESTION_TYPES: this.hasOptionsQuestionTypes
    }
  },

  methods: {
    getTypeLabel (type) {
      if (!type) return ''
      return this.$t(`${this.i18nPrefix}.questionTypeShort.${type}`)
    },

    isKnockoutOption (option) {
      return option && (option.is_knockout === true || option.is_knockout === 1 || option.is_knockout === '1')
    },

    canConfigureChildForParent (question) {
      return Boolean(question) && this.childQuestionParentTypes.includes(question.type)
    },

    getChildQuestion (optionItem) {
      // First check if child_question object is already populated
      if (optionItem.child_question) {
        return optionItem.child_question
      }
      // Then look up by child_question_id
      if (optionItem.child_question_id) {
        const childQ = _.find(this.questions, q => q.id === optionItem.child_question_id)
        if (childQ) {
          return childQ
        }
      }
      // Also check for child_question_vid
      if (optionItem.child_question_vid) {
        const childQ = _.find(this.questions, q => q._vid === optionItem.child_question_vid)
        if (childQ) {
          return childQ
        }
      }
      if (optionItem.next_question_id) {
        const childQ = _.find(this.questions, q => q.id === optionItem.next_question_id)
        if (childQ) {
          return childQ
        }
      }
      return null
    },

    getOptionChildLabel (parentQuestion, optionItem) {
      if (!parentQuestion) return ''
      if (optionItem.child_question) {
        return optionItem.child_question.title
      }
      if (optionItem.child_question_id) {
        const targetQ = _.find(this.questions, q => q.id === optionItem.child_question_id)
        return targetQ ? targetQ.title : ''
      }
      if (optionItem.child_question_vid) {
        const targetQ = _.find(this.questions, q => q._vid === optionItem.child_question_vid)
        return targetQ ? targetQ.title : ''
      }
      if (optionItem.next_question_id) {
        const targetQ = _.find(this.questions, q => q.id === optionItem.next_question_id)
        return targetQ ? targetQ.title : ''
      }
      return ''
    }
  }
}
</script>

<style scoped>
.nested-child-wrapper {
  margin-left: 20px;
}

.child-question-preview {
  background: #f5f7fa;
  border-radius: 4px;
  padding: 10px 0 10px 12px;
  margin-bottom: 8px;
  border-left: 3px solid #409eff;
}

.child-question-preview.depth-2 {
  border-left-color: #67c23a;
  background: #f0f9eb;
}

.child-question-preview.depth-3 {
  border-left-color: #e6a23c;
  background: #fdf6ec;
}

.child-question-preview.depth-4 {
  border-left-color: #f56c6c;
  background: #fef0f0;
}

.child-question-preview.depth-5 {
  border-left-color: #909399;
  background: #f4f4f5;
}

.child-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.child-content {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.child-actions {
  display: flex;
  flex: 0 0 58px;
  align-self: flex-start;
  align-items: center;
  gap: 10px;
}

.child-actions .option-action-btn {
  width: 24px;
  height: 24px;
  padding: 0;
  margin: 0;
  font-size: 18px;
  color: #0096cc;
}

.child-actions i {
  font-size: 18px;
}

.child-trigger, .child-title {
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.child-depth, .child-type {
  flex-shrink: 0;
  white-space: nowrap;
}

.child-trigger {
  color: #909399;
  font-size: 13px;
}

.child-depth {
  background: #409eff;
  color: white;
  font-size: 12px;
  padding: 2px 6px;
  border-radius: 4px;
}

.child-title {
  font-weight: 500;
  color: #303133;
}

.child-type {
  color: #909399;
  font-size: 13px;
}

.edit-child-btn {
  margin-left: auto;
}

.delete-child-btn {
}

.child-options-container {
  margin-top: 8px;
}

.child-option-list {
  margin: 0;
  padding-left: 20px;
}

.child-option-list li {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
}

.child-option-list li > span {
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.child-option-list li.knockout {
  color: #F56C6C;
}

.child-option-list .option-action-btn {
  flex-shrink: 0;
}

.next-question-preview {
  color: #0096cc;
  font-weight: 500;
}

.option-action-btn {
  padding: 0 4px;
  font-size: 12px;
}

.remove-btn {
  color: #f56c6c;
}
</style>
